import { useEffect, useMemo, useRef, useState } from 'react';

import { getQueue, type QueuedMutation } from '../offline/offlineQueue';
import { mergePendingSegments } from '../offline/pendingSegmentsFromQueue';
import type { RouteSegment } from '../types/surveillanceRoute';

const SEGMENT_OPS = new Set(['CreateRouteSegment', 'FinishRouteSegment']);

/**
 * Tope de la retención tras sincronizar (ver el bloque de abajo).
 *
 * 🔴 Sin él la retención puede quedarse para siempre: solo se suelta cuando
 * CAMBIA la firma de los tramos del servidor, y hay consumidores que no tienen
 * tramos del servidor que ofrecer —el `TimerOverlay` de la cabecera pasa la
 * lista vacía a propósito, porque sus tramos vienen de otra query—, así que su
 * firma es `''` siempre y ese cambio no llega nunca.
 *
 * El fallo se ve en carretera y es de los caros: un tramo iniciado Y cerrado
 * sin cobertura se sincroniza bien —la tabla lo enseña cerrado— pero el
 * cronómetro de la cabecera sigue contando un tramo fantasma, ofreciendo
 * «Finalizar tramo» y **tapando el botón de iniciar el siguiente**. Como la app
 * nativa no recarga nunca, el operario se queda así el resto del turno.
 *
 * 15 s es holgado para la revalidación que sigue al sync (un viaje de red) y
 * ridículo comparado con lo que duraba el fantasma.
 */
const RETENTION_DEADLINE_MS = 15_000;

/** Firma de contenido de los tramos del servidor (no de su referencia). */
function serverSignature(segments: RouteSegment[]): string {
  return segments.map((s) => `${s.id}:${s.endDate ?? ''}`).join('|');
}

/**
 * Tramos del loader + los que esperan en la cola offline.
 *
 * La cola se re-lee con el evento `offline-queue-changed` que ya emiten
 * `addToQueue`/`removeFromQueue`: al encolar aparece el tramo pendiente y al
 * replayarse desaparece, sin nada que limpiar a mano.
 *
 * ── Retención (fix del parpadeo al reconectar) ────────────────────────────
 * `syncOfflineQueue` borra cada mutation de la cola EN CUANTO se replaya, pero
 * `offline-sync-complete` —lo que dispara `useRevalidateOnSync` en
 * `RouteDetailed`— solo se emite cuando han drenado LAS TRES colas (imágenes,
 * simple y compuesta), y aún falta el viaje de red de la revalidación. En ese
 * hueco el tramo ya no está en la cola pero el loader sigue sirviendo el
 * snapshot viejo → el tramo DESAPARECÍA del mapa y la tabla y reaparecía un
 * momento después, justo al reconectar, que es cuando la patrulla está mirando.
 *
 * Por eso una operación que se va de la cola se RETIENE hasta que el servidor
 * la confirma. La confirmación es exacta, no una heurística temporal:
 * `createSegment` sella `registerInitDate` en el cliente (front #117), así que
 * el tramo real vuelve con esa misma hora y `mergePendingSegments` lo reconoce
 * y deja de pintar el fantasma.
 *
 * Red de seguridad contra fantasmas eternos: si una operación se descartó por
 * ser poison pill nunca la confirmará nadie, así que la retención se limpia
 * entera en cuanto llega una revalidación posterior al sync — a partir de ahí
 * el servidor es la única verdad. Y como esa revalidación puede no llegar
 * NUNCA para quien no fusiona tramos del servidor, la limpieza lleva además
 * plazo (`RETENTION_DEADLINE_MS`).
 */
export function usePendingSegments(
  routeId: string | undefined,
  serverSegments: RouteSegment[]
): RouteSegment[] {
  const [queue, setQueue] = useState<QueuedMutation[]>([]);
  const [retained, setRetained] = useState<QueuedMutation[]>([]);
  const lastQueueRef = useRef<QueuedMutation[]>([]);
  const awaitingRevalidateRef = useRef(false);
  const deadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const refresh = () => {
      void getQueue().then((next) => {
        if (cancelled) return;
        // Lo que había y ya no está = replayado (o descartado): se retiene
        // hasta que el servidor lo confirme.
        const vanished = lastQueueRef.current.filter(
          (op) =>
            SEGMENT_OPS.has(op.operationName) &&
            !next.some((pending) => pending.id === op.id)
        );
        lastQueueRef.current = next;
        setQueue(next);
        if (vanished.length > 0) {
          setRetained((current) => [
            ...current,
            ...vanished.filter((op) => !current.some((r) => r.id === op.id)),
          ]);
        }
      });
    };

    const markAwaitingRevalidate = () => {
      awaitingRevalidateRef.current = true;
      // Plazo: si la revalidación no llega (o el consumidor no tiene ninguna),
      // se suelta igual. Ver `RETENTION_DEADLINE_MS`.
      if (deadlineRef.current) clearTimeout(deadlineRef.current);
      deadlineRef.current = setTimeout(() => {
        deadlineRef.current = null;
        awaitingRevalidateRef.current = false;
        setRetained([]);
      }, RETENTION_DEADLINE_MS);
    };

    refresh();
    window.addEventListener('offline-queue-changed', refresh);
    // Al recuperar la red el sync manager vacía la cola; el evento ya lo
    // cubre, pero `online` da una segunda oportunidad por si el replay se
    // adelantó al montaje de esta página.
    window.addEventListener('online', refresh);
    window.addEventListener('offline-sync-complete', markAwaitingRevalidate);
    return () => {
      cancelled = true;
      if (deadlineRef.current) clearTimeout(deadlineRef.current);
      window.removeEventListener('offline-queue-changed', refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener(
        'offline-sync-complete',
        markAwaitingRevalidate
      );
    };
  }, []);

  const signature = serverSignature(serverSegments);

  // Llegó dato nuevo del servidor DESPUÉS de sincronizar: se suelta la
  // retención entera. Lo que el servidor no traiga a estas alturas no va a
  // llegar (p.ej. una operación descartada por inválida).
  useEffect(() => {
    if (!awaitingRevalidateRef.current) return;
    awaitingRevalidateRef.current = false;
    if (deadlineRef.current) {
      clearTimeout(deadlineRef.current);
      deadlineRef.current = null;
    }
    setRetained([]);
  }, [signature]);

  return useMemo(
    () =>
      mergePendingSegments(serverSegments, [...queue, ...retained], routeId),
    [serverSegments, queue, retained, routeId]
  );
}
